import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatLuxonDateModule, provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { ErrorStateMatcher, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { AppEvent } from '../../../models/app-event';
import { AttendanceType } from '../../../models/enums';
import { LastUpdatePipe } from '../../../pipes/last-update.pipe';
import { EventService, EventSimilarityCheckPayload } from '../../../services/event.service';
import { ReferenceDataService } from '../../../services/reference-data.service';
import { MY_DATE_FORMATS, normalizeDate } from '../../../utils/utils';
import { AdvancedChipSelectComponent } from '../../misc/advanced-chip-select/advanced-chip-select.component';
import { separatorKeysCodes } from '../../misc/nested-options';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { of, delay, combineLatest, debounceTime, distinctUntilChanged, map, startWith, skip, filter } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SimilarityListPipe } from '../../../pipes/similarity-list.pipe';
import { OrganisationSimilarityCheckPayload } from '../../../services/organisation.service';
import { DateTime } from 'luxon';
import { ProjectService } from '../../../services/project.service';
import { EventSimilarityListPipe } from '../../../pipes/event-similarity-list.pipe';
import { SimilarityList } from '../../../models/similarity-list';
import { DateSimilarityListPipe } from '../../../pipes/date-similarity-list.pipe';
import { HistoryPanelComponent } from '../../misc/history-panel/history-panel.component';
import { O365AuthService } from '../../../services/o365-auth.service';

export class EventTypesErrorStateMatcher implements ErrorStateMatcher {
  constructor(private form?: FormGroup) { }

  isErrorState(): boolean {
    const control = this.form?.get('eventTypes');
    const invalid = !!(control && control.invalid && (control.touched || control.dirty));
    return invalid;
  }
}

@Component({
  selector: 'app-edit-event',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatLuxonDateModule,
    MatDialogModule,
    MatDatepickerModule,
    MatOptionModule,
    MatButtonModule,
    MatSelectModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    MatSnackBarModule,
    LastUpdatePipe,
    AdvancedChipSelectComponent,
    MatProgressSpinnerModule,
    SimilarityListPipe,
    EventSimilarityListPipe,
    HistoryPanelComponent,
  ],
  providers: [
    provideLuxonDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'fr' },
  ],
  templateUrl: './edit-event.component.html',
  styleUrl: './edit-event.component.scss'
})
export class EditEventComponent implements OnInit {
  readonly attendanceOptions = [
    { value: AttendanceType.ONLINE, label: 'Online' },
    { value: AttendanceType.IN_PERSON, label: 'In person' },
    { value: AttendanceType.HYBRID, label: 'Hybrid' }
  ];

  readonly _snackBar = inject(MatSnackBar);
  readonly separatorKeysCodes = separatorKeysCodes;
  form: FormGroup;

  mode: string = 'Create'; // Create, Update or Display
  event: AppEvent = new AppEvent();
  resetCounter = 0; // used to reset the filter form

  private destroyRef = inject(DestroyRef);

  readonly refDataService = inject(ReferenceDataService);
  readonly projectService = inject(ProjectService);
  readonly authService = inject(O365AuthService);

  readonly allTopics = this.refDataService.getGeneralTopicsOfInterest();
  readonly allTopicOptions = computed(() => this.allTopics());

  readonly allEventTypes = this.refDataService.getEventTypes();
  readonly allEventTypeOptions = computed(() => this.allEventTypes());

  readonly allProjects = toSignal(this.projectService.getAllProjects(), { initialValue: [] });
  readonly allProjectOptions = computed(() => this.allProjects());

  readonly allUsers = computed(() => {
    // ... implies making a copy and working on the copy
    return [...this.refDataService.getGraphUsers()()].sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );
  });


  private checkDuplicateParams = signal<EventSimilarityCheckPayload | undefined>(undefined);

  duplicateEntryResource = rxResource<SimilarityList<any> | null, EventSimilarityCheckPayload | undefined>({
    params: this.checkDuplicateParams,
    stream: ({ params }) => {
      if (!params) {
        return of(null);
      }
      return this.eventService.checkForSimilarEvents(params).pipe(delay(250));;
    },
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly eventService: EventService,
    private readonly dialog: MatDialog,) {
    this.form = {} as FormGroup;
  }

  ngOnInit(): void {
    this.buildForm();
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam === null) {
      this.mode = 'Create';
      this.initEmptyForm();
    } else {
      const eventId = Number(idParam);
      if (!idParam || isNaN(eventId)) {
        this.router.navigate(['/events'], {
          state: { message: '❌ No such event: ' + idParam }
        });
        return;
      }
      this.mode = 'Update';
      this.eventService.getEventById(eventId).subscribe({
        next: (event: AppEvent) => {
          this.event = event;
          this.form.patchValue({
            ...this.event,
            projects: this.event.projects
          });
        },
        error: (err: string) => {
          // handle error (e.g. show toast, redirect, etc.)
          console.error('Failed to load event:', err);
          this.router.navigate(['/events'],
            {
              state: { message: '❌ Failed to load event: ' + eventId }
            }
          );
        }
      });
    }
  }

  initEmptyForm(): void {
    this.event = new AppEvent();
  }

  cancel(): void {
    this.router.navigate(['/events']);
  }

  save(): void {
    console.log('Save event', this.mode, this.form.value);
    if (this.form.valid) {
      const updatedEvent: AppEvent = {
        ...this.event,
        ...this.form.value
      };
      let rawValue = this.form.get('date')?.value;
      let isoDate = normalizeDate(rawValue);
      if (!isoDate) {
        this._snackBar.open('⚠️ Invalid event date', 'Close', { duration: 3000 });
        return;
      }
      updatedEvent.date = isoDate

      rawValue = this.form.get('endDate')?.value;
      if (rawValue) {
        isoDate = normalizeDate(rawValue);
        if (!isoDate) {
          this._snackBar.open('⚠️ Invalid event end date', 'Close', { duration: 3000 });
          return;
        }
        updatedEvent.endDate = isoDate;
      } else {
        updatedEvent.endDate = null;
      }
      if (this.mode === 'Create') {
        console.log('new event: ', updatedEvent);
        this.eventService.create(updatedEvent).subscribe({
          next: () => {
            this.router.navigate(['/events'],
              {
                state: { message: '✅ Event created' }
              }
            );
          },
          error: (err) => {
            console.debug('error creating event', err)
            this._snackBar.open('❌ Error creating event', 'Close', { duration: 3000 });
          }
        })
      } else {
        console.log('updatedEvent: ', updatedEvent);
        this.eventService.update(updatedEvent).subscribe({
          next: () => {
            this.router.navigate(['/events'],
              {
                state: { message: '✅ Event updated' }
              }
            );
          },
          error: (err) => {
            console.debug('error updating event', err)
            this._snackBar.open('❌ Error updating event', 'Close', { duration: 3000 });
          }
        })
      }
    }
  }

  delete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Event',
        message: 'Are you sure you want to delete this event?'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.eventService.delete(this.event!.id).subscribe({
          next: () => {
            this.router.navigate(['/events'],
              {
                state: { message: '✅ Event deleted' }
              }
            );
          },
          error: (err) => {
            console.debug('error deleting event', err)
            this._snackBar.open(`❌ Error deleting event: ${err}`, 'Close', { duration: 3000 });
          }
        })
      }
    });
  }

  private buildForm(): void {
    this.form = this.fb.group({
      name: [this.event?.name || '', Validators.required],
      description: [this.event?.description || ''],
      date: [this.event?.date || '', Validators.required],
      endDate: [this.event?.endDate || ''],
      attendanceType: [this.event?.attendanceType || '', Validators.required],
      responsiblePerson: [this.event?.responsiblePerson || '', Validators.required],
      driveFolder: [this.event?.driveFolder || ''],
      website: [this.event?.website || ''],
      topics: [],
      projects: [],
      eventTypes: [],
    });

    combineLatest([
      this.form.get('name')!.valueChanges.pipe(
        startWith('')),
      this.form.get('date')!.valueChanges.pipe(
        startWith(''))
    ]).pipe(
      skip(1),
      map(([name, date]) => ({
        id: this.event.id,
        name: (name ?? '').trim(),
        date: (date instanceof DateTime ? date.toISODate() : '')
      }) as EventSimilarityCheckPayload),
      filter(payload => payload.name !== '' || payload.date !== ''),
      debounceTime(300),
      distinctUntilChanged((prev, curr) =>
        prev.name === curr.name && prev.date === curr.date
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(payload => {
      this.checkDuplicateParams.set(payload);
    });
  }

  openLink(): void {
    let url = this.form.get('website')?.value
    if (url) {
      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
      }
      window.open(url, '_blank')
    }
  }

  copyLink(): void {
    let url = this.form.get('website')?.value
    if (url) {
      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
      }
      navigator.clipboard.writeText(url).then(() => {
        console.log('Copied to clipboard!');
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    }
  }
}
